
import React, { useEffect } from "react";
import { isBrowser } from "@/utils/runtime";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useLeadCapture } from "@/hooks/useLeadCapture";
import TocFotosFooter from "@/components/TocFotosFooter";

// Category card component
const CategoryCard: React.FC<{ name: string; imageUrl: string }> = ({ name, imageUrl }) => (
  <div className="relative overflow-hidden rounded-lg shadow-md hover:shadow-lg transition-all group h-40">
    <div 
      className="absolute inset-0 bg-cover bg-center opacity-50 group-hover:opacity-70 transition-opacity"
      style={{ backgroundImage: `url(${imageUrl})` }}
    />
    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
    <div className="absolute inset-0 flex items-center justify-center p-4">
      <h3 className="text-white font-bold text-xl text-center shadow-text">{name}</h3>
    </div>
  </div>
);

const TocFotosEventos01 = () => {
  // Set page title for SEO
  useEffect(() => {
    if (!isBrowser) return;
    globalThis.document.title = "Toc Fotos Eventos | Capture sua paixão. Reviva o momento. Compre sua foto.";

    // Add meta description
    const metaDescription = globalThis.document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute("content",
        "Encontre e compre suas fotos praticando suas atividades favoritas em eventos por todo o Brasil.");
    }
  }, []);
  
  const { openLeadCapture, LeadCaptureModalComponent } = useLeadCapture({
    title: "Seja um fotógrafo parceiro",
    description: "Preencha seus dados para começar a vender suas fotos",
    onSuccess: () => {
      if (isBrowser) {
        globalThis.scrollTo({ top: 0, behavior: "smooth" });
      }
    }
  });

  // Categories for the grid
  const categories = [
    { name: "Futebol", imageUrl: "/lovable-uploads/f2506d8b-d1d9-4850-84af-5b0a529302ba.png" },
    { name: "Corrida", imageUrl: "/lovable-uploads/e4405c79-3173-4e9c-81d5-25973e61413d.png" },
    { name: "Beach Tennis", imageUrl: "/lovable-uploads/5d0bfcd5-8f46-490c-8e19-f0153b1a59b3.png" },
    { name: "Ciclismo", imageUrl: "/lovable-uploads/9d9dee7d-2850-451a-9469-91caabdf7f0c.png" },
    { name: "Crossfit", imageUrl: "/lovable-uploads/cbb23aa4-bf2c-4d20-8c67-3e50fed861a3.png" },
    { name: "Natação", imageUrl: "/lovable-uploads/e4405c79-3173-4e9c-81d5-25973e61413d.png" },
    { name: "Vôlei", imageUrl: "/lovable-uploads/f2506d8b-d1d9-4850-84af-5b0a529302ba.png" },
    { name: "Futevôlei", imageUrl: "/lovable-uploads/5d0bfcd5-8f46-490c-8e19-f0153b1a59b3.png" }
  ];

  // FAQ items
  const faqItems = [
    {
      question: "O que é a TocFotosEventos?",
      answer: "TocFotosEventos é uma plataforma que conecta pessoas a fotógrafos profissionais, permitindo encontrar, visualizar e comprar fotos de eventos esportivos, shows, formaturas e muito mais em todo o Brasil."
    },
    {
      question: "Como encontrar minhas fotos?",
      answer: "Você pode buscar suas fotos pelo nome do evento, data, local ou até mesmo pelos filtros de categoria disponíveis na página de busca. Nossa tecnologia facilita a localização das suas imagens."
    },
    {
      question: "Posso remover a marca d'água?",
      answer: "Sim. Ao comprar a foto, você recebe a versão em alta resolução sem marca d'água, pronta para impressão ou compartilhamento digital."
    },
    {
      question: "Minhas fotos ficam disponíveis por quanto tempo?",
      answer: "As fotos ficam disponíveis na plataforma por até 12 meses após o evento. Recomendamos salvar suas fotos após a compra para garantir o acesso permanente."
    },
    {
      question: "Como contratar um fotógrafo para meu evento?",
      answer: "Na seção 'Fotógrafos', você pode encontrar profissionais disponíveis na sua região, verificar portfólios e entrar em contato para solicitar orçamentos para seu evento."
    },
    {
      question: "Como me tornar um fotógrafo parceiro?",
      answer: "Clique no botão 'Quero começar agora', preencha o formulário com seus dados e portfólio. Nossa equipe entrará em contato para validar seu perfil e fornecer acesso à plataforma."
    }
  ];

  return (
    <div className="min-h-screen flex flex-col bg-white font-sans">
      {LeadCaptureModalComponent}
      
      <header className="border-b border-gray-100 py-4">
        <div className="container max-w-7xl mx-auto px-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold text-[#323031]">TocFotosEventos</h1>
          <nav className="hidden md:flex space-x-6">
            <a href="#" className="text-gray-700 hover:text-[#FF5605] transition-colors">Início</a>
            <a href="#" className="text-gray-700 hover:text-[#FF5605] transition-colors">Eventos</a>
            <a href="#" className="text-gray-700 hover:text-[#FF5605] transition-colors">Fotógrafos</a>
            <a href="#" className="text-gray-700 hover:text-[#FF5605] transition-colors">Como funciona</a>
            <a href="#" className="text-gray-700 hover:text-[#FF5605] transition-colors">Contato</a>
          </nav>
          <Button variant="outline" className="border-[#3322FC] text-[#3322FC] hover:bg-[#3322FC] hover:text-white">
            Entrar
          </Button>
        </div>
      </header>

      <main>
        {/* Hero Section */}
        <section className="py-16 md:py-24">
          <div className="container max-w-7xl mx-auto px-4 text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6 text-[#323031]">
              Capture sua paixão. Reviva o momento. Compre sua foto.
            </h1>
            <p className="text-lg md:text-xl text-gray-700 max-w-3xl mx-auto mb-10">
              Encontre e compre suas fotos praticando suas atividades favoritas em eventos por todo o Brasil.
            </p>
            <div className="max-w-2xl mx-auto flex flex-col sm:flex-row gap-2">
              <Input 
                type="text" 
                placeholder="Pesquise pelo nome, evento ou data…" 
                className="flex-1 h-12"
              />
              <Button 
                className="h-12 px-6 bg-[#FF5605] hover:bg-[#FF5605]/90 text-white flex items-center gap-2"
              >
                <Search size={18} /> Buscar
              </Button>
            </div>
          </div>
        </section>
        
        {/* Categories Section */}
        <section className="py-16 bg-gray-50">
          <div className="container max-w-7xl mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-semibold mb-8 text-center text-[#323031]">
              Categorias em Destaque
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {categories.map((category, index) => (
                <CategoryCard key={index} name={category.name} imageUrl={category.imageUrl} />
              ))}
            </div>
            <div className="mt-8 text-center">
              <Button variant="outline" className="border-[#3322FC] text-[#3322FC] hover:bg-[#3322FC] hover:text-white mt-4">
                Ver todas as categorias
              </Button>
            </div>
          </div>
        </section>
        
        {/* Value Proposition */}
        <section className="py-16">
          <div className="container max-w-7xl mx-auto px-4 text-center">
            <div className="max-w-3xl mx-auto">
              <p className="text-lg md:text-xl text-gray-700 mb-4">
                Uma plataforma feita para quem vive o momento e quer guardá-lo para sempre.
              </p>
              <p className="text-lg md:text-xl text-gray-700">
                Na TocFotosEventos, você encontra os melhores fotógrafos, imagens em alta qualidade e uma experiência de compra prática, rápida e segura.
              </p>
            </div>
          </div>
        </section>
        
        {/* Testimonial */}
        <section className="py-16 bg-gray-50">
          <div className="container max-w-7xl mx-auto px-4">
            <div className="max-w-3xl mx-auto border-l-4 border-[#3322FC] pl-6 py-2 bg-white rounded shadow-sm">
              <p className="text-lg md:text-xl italic text-gray-700 mb-4">
                "Nunca imaginei que uma simples foto do meu campeonato de beach tennis pudesse me emocionar tanto. A TocFotosEventos é sensacional!"
              </p>
              <p className="text-sm font-semibold text-[#323031]">
                — Fernanda Braga, Rio de Janeiro – RJ
              </p>
            </div>
          </div>
        </section>
        
        {/* Photographer CTA */}
        <section className="py-16">
          <div className="container max-w-7xl mx-auto px-4 text-center">
            <h2 className="text-2xl md:text-3xl font-semibold mb-4 text-[#323031]">
              Faça parte da nossa comunidade de fotógrafos!
            </h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto mb-8">
              Você é fotógrafo e quer vender suas fotos? Aqui você encontra visibilidade, suporte e liberdade.
            </p>
            <Button 
              onClick={() => openLeadCapture()}
              className="bg-[#FF5605] hover:bg-[#FF5605]/90 text-white px-8 py-6 rounded-full text-lg"
            >
              Quero começar agora
            </Button>
          </div>
        </section>
        
        {/* FAQ Section */}
        <section className="py-16 bg-gray-50">
          <div className="container max-w-7xl mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-semibold mb-8 text-center text-[#323031]">
              Dúvidas Frequentes
            </h2>
            <div className="max-w-3xl mx-auto">
              <Accordion type="single" collapsible className="bg-white rounded-lg shadow">
                {faqItems.map((item, index) => (
                  <AccordionItem key={index} value={`item-${index}`} className="border-b border-gray-200 last:border-b-0">
                    <AccordionTrigger className="px-6 py-4 text-left font-medium text-[#323031] hover:text-[#FF5605]">
                      {item.question}
                    </AccordionTrigger>
                    <AccordionContent className="px-6 pb-4 text-gray-700">
                      {item.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>
      </main>
      
      <TocFotosFooter />
    </div>
  );
};

export default TocFotosEventos01;
